/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ProjectMember } from '@touchplan/common';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
import { CalendarWorkdays } from '@touchplan/ticket';
/**
 * The object that this service emits whenever the authedProjectMember changes.
 */
export interface IWorkingDayProviders {
	projectMember: ProjectMember;
	workDays: CalendarWorkdays;
}
/**
 * ProjectWorkingDayService
 * The intention for this Angular service is to provide a connection to the following data sources that are currently on planState:
 * 	- authedProjectMember$
 * 	- CalendarWorkDays (workDays)
 * Currently these live either directly on plan state (authedProjectMember$) or is a private variable on PlanComponent.
 * So this class collects the data into a singular source and exposes it to our canvas code, so we can properly draw the column state.
 * You can treat this class as a facade around the authedProjectMember$ and CalendarWorkDays
 */
@Injectable({ providedIn: 'root' })
export class ProjectWorkingDayService extends AbstractCanvasConnection<IWorkingDayProviders> {
	/**
	 * The currently loaded CalendarWorkDays. This is passed in from the PlanComponent and is used to determine if
	 * a specific day is considered a work day or not.
	 * @private
	 */
	private _workDays: CalendarWorkdays;
	/**
	 * The current project member (last emission from the authedProjectMember$)
	 * @private
	 */
	private _currentProjectMember: ProjectMember;
	/**
	 * Registers the service to receive the following data:
	 * 	- authedProjectMember$: The current Project Member, used to determine the current role
	 * 	- workDays: CalendarWorkDays, object that takes multiple sources of data (exception days, work days and role schedules)
	 * 			and provides an API for determining if a given day is a working day or not.
	 * @param authProjectMember$ Observable stream of authenticated project member
	 * @param workDays CalendarWorkDays Object for CalendarWorkDays API
	 */
	public registerPlanData(authProjectMember$: Observable<ProjectMember>, workDays: CalendarWorkdays): void {
		this.clearPreviousSubscriber();
		this._workDays = workDays;
		this._subscription = authProjectMember$.subscribe((data: ProjectMember) => {
			this._currentProjectMember = data;
			this._changed$.next({ projectMember: data, workDays: this._workDays });
		});
	}
	/**
	 * Returns true if the provided date is a work day. Simply returns the result of the CalendarWorkDays.isWorkDay() value
	 * Add additional API around the Project Member and CalendarWorkDays as needed.
	 * @param date
	 */
	public isWorkDay(date: string): boolean {
		return this._workDays.isWorkday(date, this._currentProjectMember?.view?.roleId);
	}
	/**
	 * Cleans up any existing subscription before setting up a new one.
	 * Overrides the parent to clear additional data
	 */
	protected override clearPreviousSubscriber(): void {
		if (!this._subscription) {
			return;
		}
		this._workDays.destroy();
		this._workDays = null;

		this._subscription.unsubscribe();
		this._subscription = null;

		this._currentProjectMember = null;
	}
}
